import { InfiniteScrollGrid, useBaseStore } from '@roc/feature-app-core';
import { useLoanRoutes, usePlatformLoanRoutes } from '@roc/feature-loans-routes-config';
import { CapitalProvider, CapitalProviderLabel, capitalProviderOptions, formatDate } from '@roc/feature-utils';
import { useLoanStore } from '../hooks/useLoanStore';
import {
  AgGridColumnProps,
  DataGrid,
  LinkColumn,
  SelectFilterComponent,
  SelectFloatingFilterComponent,
  StatusChip,
  StatusType,
  CrescentSpinner,
} from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { usePayoffRequestStore } from '../loanDetails/modules/loanRequests/hooks/usePayoffRequestStore';
import { Box } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

const noOpComparator = () => 0;

const isExternalCapital = row => {
  return row?.capitalProvider === CapitalProvider.EXTERNAL_CAPITAL || row?.capitalProvider === CapitalProviderLabel.EXTERNAL_CAPITAL;
};

const addCapitalProviderColumn = columns => {
  const capitalProviderColumn = {
    field: 'capitalProvider',
    headerName: 'Capital Provider',
    minWidth: 150,
    filter: 'selectFilter',
    floatingFilter: true,
    floatingFilterComponent: 'selectFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      options: capitalProviderOptions,
    },
  };
  const [firstColumn, ...otherColumns] = columns;
  return [firstColumn, capitalProviderColumn, ...otherColumns];
};

const columns: AgGridColumnProps[] = [
  {
    field: 'payoffNumber',
    headerName: 'Payoff #',
    minWidth: 130,
    cellRenderer: 'payoffNumberCellRenderer',
    comparator: noOpComparator,
    pinned: 'left',
    lockPinned: true,
    sortable: false,
  },
  {
    field: 'loanId',
    headerName: 'Loan Id',
    minWidth: 100,
    cellRenderer: 'loanIdCellRenderer',
    filter: 'agTextColumnFilter',
    comparator: noOpComparator,
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'payoffStatus',
    headerName: 'Status',
    minWidth: 190,
    maxWidth: 300,
    comparator: noOpComparator,
    filter: 'selectFilter',
    cellRenderer: 'statusCellRenderer',
    floatingFilter: true,
    floatingFilterComponent: 'selectFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      options: [
        { label: 'Payoff Requested', value: 'Payoff Requested' },
        {
          label: 'Forwarded to Servicer',
          value: 'Forwarded to Servicer',
        },
        { label: 'Approval & Forwarding Pending', value: 'Approval & Forwarding Pending' },
        { label: 'Approved', value: 'Approved' },
        { label: 'Retracted', value: 'Retracted' },
      ],
    },
  },
  {
    field: 'requestedDate',
    headerName: 'Request Date',
    minWidth: 120,
    filter: 'agTextColumnFilter',
    comparator: noOpComparator,
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'goodThroughDate',
    headerName: 'Good Through Date',
    minWidth: 130,
    cellRenderer: 'goodThroughDateCellRenderer',
    comparator: noOpComparator,
    filter: 'agDateColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
];

export const MyPayoffsGrid = observer(props => {
  const { payoffRequestStore } = usePayoffRequestStore();
  const { globalStore } = useBaseStore();
  const { loanStore } = useLoanStore();
  const { loanRoutesConfig } = useLoanRoutes();
  const { platformLoanRoutesConfig } = usePlatformLoanRoutes();

  const gridStore = globalStore.showUnifiedLoans
    ? loanStore.unifiedMyPayoffsGridStore
    : payoffRequestStore.myPayoffsGridStore;

  const _columns = globalStore.showUnifiedLoans
    ? addCapitalProviderColumn(columns)
    : columns;

  return (
    <InfiniteScrollGrid
      toolbar={props.toolbar}
      columns={_columns}
      frameworkComponents={getFrameworkComponents(loanRoutesConfig, platformLoanRoutesConfig)}
      store={gridStore}
    />
  );
});

export const MyPayoffsGridPaginated = observer(props => {
  const { payoffRequestStore } = usePayoffRequestStore();
  const { globalStore } = useBaseStore();
  const { loanStore } = useLoanStore();
  const { loanRoutesConfig } = useLoanRoutes();
  const { platformLoanRoutesConfig } = usePlatformLoanRoutes();

  const myPayoffsGridStore = globalStore.showUnifiedLoans
    ? loanStore.unifiedBorrowerPayoffsGridStore
    : payoffRequestStore.myPayoffsGridStore;

  const _columns = globalStore.showUnifiedLoans
    ? addCapitalProviderColumn(columns)
    : columns;

  useEffect(() => {
    myPayoffsGridStore.resetAndFetchGridData();
  }, []);

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    myPayoffsGridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    myPayoffsGridStore.setFilterModel(filterModel);
  };

  return (
    <DataGrid
      toolbar={props.toolbar}
      columns={_columns}
      rows={myPayoffsGridStore.gridData.data.rows}
      frameworkComponents={getFrameworkComponents(
        loanRoutesConfig,
        platformLoanRoutesConfig
      )}
      onSortChanged={onSortChanged}
      onFilterChanged={onFilterChanged}
      isLoading={myPayoffsGridStore.isLoading}
      showFilters={myPayoffsGridStore.showFilters}
      sortModel={myPayoffsGridStore.sortModel}
      filterModel={myPayoffsGridStore.filterModel}
      paginationData={myPayoffsGridStore.gridData.meta}
      setPageNumber={myPayoffsGridStore.setPageNumber}
      domLayout="autoHeight"
      isError={myPayoffsGridStore.isError}
    />
  );
});

const getFrameworkComponents = (loanRoutesConfig, platformLoanRoutesConfig) => {
  return {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    dateCellRenderer: ({ value }) => {
      return value ? formatDate(value, 'MM/dd/yyyy') : '';
    },
    payoffNumberCellRenderer: params => {
      if (!params.value) {
        return '';
      }
      const payoffId = params.node.data?.loanPayoffData;
      const loanId = params.node.data?.loanId;
      const { onClick } = params;
      const value = `${loanId} - Payoff ${params.value}`;
      const url = isExternalCapital(params.node.data)
        ? platformLoanRoutesConfig.loans(loanId).children.dashboard.url
        : loanRoutesConfig.loans(loanId).children.payoffRequest.url;
      return LinkColumn({
        value: value,
        onClick,
        url,
      });
    },
    loanIdCellRenderer: params => {
      if (!params.value) {
        return '';
      }
      const { onClick } = params;
      const url = isExternalCapital(params.node.data)
        ? platformLoanRoutesConfig.loans(params.value).children.dashboard.url
        : loanRoutesConfig.loans(params.value).children.payoffRequest.url;
      return LinkColumn({
        value: params.value,
        onClick,
        url,
      });
    },
    statusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip
          statusType={StatusType.PAYOFFS}
          label={value}
          size="small"
          variant={value == 'Approved' ? 'filled' : 'outlined'}
        />
      ) : null,
  };
};

export const RecentPayoffsGrid = observer(({
  onDataLoad
}: {
  onDataLoad?: (rows: any[], totalCount: number) => void
}) => {
  const { payoffRequestStore } = usePayoffRequestStore();
  const { globalStore } = useBaseStore();
  const { loanStore } = useLoanStore();
  const { loanRoutesConfig } = useLoanRoutes();
  const { platformLoanRoutesConfig } = usePlatformLoanRoutes();
  const [dataRows, setDataRows] = useState([]);

  const myPayoffsGridStore = globalStore.showUnifiedLoans
    ? loanStore.unifiedBorrowerPayoffsGridStore
    : payoffRequestStore.myPayoffsGridStore;

  useEffect(() => {
    myPayoffsGridStore.resetAndFetchGridData();
  }, []);

  useEffect(() => {
    if (myPayoffsGridStore.gridData?.data?.rows?.length) {
      const slicedData = myPayoffsGridStore.gridData?.data?.rows.slice(0, 5);
      setDataRows(slicedData);
      onDataLoad && onDataLoad(slicedData, myPayoffsGridStore.gridData?.data?.rows?.length);
    } else {
      setDataRows([]);
      onDataLoad && onDataLoad([], 0)
    }
  }, [myPayoffsGridStore.gridData.data.rows]);

  const recentPayoffsGridColumns = columns.filter((col) => {
    return col.field != 'loanId'
  }).map((col) => {
    return {
      ...col,
      sortable: false,
      floatingFilter: false,
      lockPinned: false,
      pinned: false,
    }
  });

  return (
    <>
      {
        myPayoffsGridStore.isLoading &&
        <Box display="flex" justifyContent="center" py={3}>
          <CrescentSpinner size={32} />
        </Box>
      }
      {
        !myPayoffsGridStore.isLoading && dataRows?.length == 0 &&
        <Box m={2}>
          <Alert severity="info">
            You haven't requested a payoff for any of your loans recently.
          </Alert>
        </Box>
      }
      {
        !myPayoffsGridStore.isLoading && dataRows?.length > 0 &&
        <DataGrid
          columns={recentPayoffsGridColumns}
          rows={dataRows}
          frameworkComponents={getFrameworkComponents(loanRoutesConfig, platformLoanRoutesConfig)}
          isLoading={myPayoffsGridStore.isLoading}
          domLayout="autoHeight"
          isError={myPayoffsGridStore.isError}
        />
      }
    </>
  );
});

export default MyPayoffsGrid;
